/**
 * Embedded screens whose iframe is as tall as its content: the CRM card tab
 * and the embedded dashboard tell Bitrix24 their height, so their layout must
 * not pin a viewport-high box around it — `html`/`body` (`height: 100%; overflow: hidden`), a `h-screen` /
 * `min-h-screen` wrapper — a box like that keeps the document at least as tall
 * as the frame is now, and the frame could only ever grow. Every other
 * embedded screen is a full window. The same goes for what such a screen
 * shows: a viewport-high box inside it (`h-screen`) is as tall as the frame,
 * so the content is always taller than the frame, the host grows it, the box
 * grows with it — a loop; a tab with such a need gets a height of its own.
 */
import type { B24Frame } from '@bitrix24/b24jssdk';

/**
 * The height of an open record's report. The iframe is as tall as its content,
 * so the viewport (`100vh`) is only the last height the frame was fitted to — a
 * short overview would squeeze the report with it, and a box as tall as the
 * viewport would grow the frame it is measured by, without end.
 * `RecordDetailInline`'s inner flex chain needs a definite height to anchor
 * on; no token is a height.
 */
export const RECORD_REPORT_HEIGHT_PX = 720;

/**
 * The least height of a content-sized screen. `position: fixed` boxes (dialogs,
 * the release pop-up, menus) are placed in the iframe's own viewport, which the
 * host does not scroll: on a frame as short as an empty list they are cut off.
 */
export const EMBEDDED_MIN_FRAME_HEIGHT_PX = 600;

const CRM_TAB_PATH = '/embedded/crm-tab';
const DASHBOARD_PATH = '/embedded/dashboard';

/** The attribute on `<html>` that lets the document be as tall as the content. */
export const CONTENT_SIZED_ATTRIBUTE = 'data-content-sized';

/** The CRM card tab (#745) and anything under it — the ONE check for the tab. */
export function isCrmTabPath(pathname: string): boolean {
  return pathname === CRM_TAB_PATH || pathname.startsWith(`${CRM_TAB_PATH}/`);
}

/** The embedded dashboard (the tab strip) and anything under it. */
export function isEmbeddedDashboardPath(pathname: string): boolean {
  return pathname === DASHBOARD_PATH || pathname.startsWith(`${DASHBOARD_PATH}/`);
}

export function isContentSizedEmbeddedPath(pathname: string): boolean {
  return isCrmTabPath(pathname) || isEmbeddedDashboardPath(pathname);
}

/**
 * Marks (or unmarks) the document as content-sized for `pathname`; the CSS
 * keyed on the attribute turns `html`/`body` from a viewport-high clipping box
 * into ordinary flow. Returns whether it is marked.
 */
export function syncContentSizedDocument(
  pathname: string,
  root: HTMLElement = document.documentElement
): boolean {
  const contentSized = isContentSizedEmbeddedPath(pathname);
  if (contentSized) root.setAttribute(CONTENT_SIZED_ATTRIBUTE, '');
  else root.removeAttribute(CONTENT_SIZED_ATTRIBUTE);
  return contentSized;
}

type FrameParent = Pick<B24Frame['parent'], 'fitWindow'> & {
  message: Pick<B24Frame['parent']['message'], 'send'>;
};

/**
 * Tell Bitrix24 the height of a content-sized screen, without pinning the
 * width: `fitWindow()` measures the root element, whose scroll height is never
 * below the viewport — it can grow the frame but never shrink it — and asked the
 * host for `100%`, and so does this: the screen fills whatever width the portal
 * pane has, now and after the browser window is resized. `resizeWindowAuto()`
 * would freeze it at the width of the moment (the body's width in pixels).
 *
 * The height is the body's box, rounded up — not `scrollHeight`, which counts
 * a box that is mid-way through an enter animation at its offset and would
 * leave the frame too tall once the animation ends, with nothing to correct it.
 * A body of no height (the frame is hidden, nothing is drawn yet) is not sent:
 * the host would collapse the frame to nothing.
 */
export function fitContentHeightFrame(
  parent: Pick<FrameParent, 'message'>
): Promise<void> {
  const height = Math.ceil(document.body.getBoundingClientRect().height);
  if (height <= 0) return Promise.resolve();
  return parent.message.send('resizeWindow', {
    width: '100%',
    height,
    isSafely: true,
  });
}

/**
 * Size the frame for the screen at `pathname`: by content on the CRM tab and
 * the dashboard, to the document elsewhere.
 */
export function fitEmbeddedFrame(parent: FrameParent, pathname: string): Promise<void> {
  return isContentSizedEmbeddedPath(pathname)
    ? fitContentHeightFrame(parent)
    : parent.fitWindow();
}

/** How far above a scrolled-to element the portal page is left, in pixels. */
export const PARENT_SCROLL_MARGIN_PX = 16;

/**
 * Bring `element` into view in the Bitrix24 page. The iframe is as tall as its
 * content and scrolls in the portal page, which `scrollIntoView` inside a
 * cross-origin frame does not reach; the SDK can only SET that scroll
 * (`scrollParentWindow`, an absolute position in the portal page, 0 — the very
 * top) and cannot read it, nor where the frame sits in the page — so «already
 * in view, stay» cannot be told either.
 *
 * So the position is the element's own in the frame, less a margin. The portal
 * page has the frame's offset (its header, the tab strip) on top of that, so the
 * element lands that much BELOW the top of the window and is never cut off above
 * it — which is the side that matters: a report whose header is out of sight.
 *
 * The frame is fitted to the content first: when the document has just got
 * taller («Back» to a long list), the page is still as short as the old frame,
 * and the host would clamp the scroll to its end. The host handles the two
 * messages in the order they are sent, so the fit is not waited for (its
 * acknowledgement can take a second): both go out now, from the box as it is
 * now — a wait would let the element leave the page, or another scroll
 * overtake this one, in between.
 */
export function scrollParentToElement(
  parent: Pick<FrameParent, 'message'> & Pick<B24Frame['parent'], 'scrollParentWindow'>,
  element: Element
): Promise<void> {
  const top = element.getBoundingClientRect().top + window.scrollY;
  const fit = fitContentHeightFrame(parent);
  const scroll = parent.scrollParentWindow(
    Math.max(0, Math.round(top - PARENT_SCROLL_MARGIN_PX))
  );
  return Promise.all([fit, scroll]).then(() => undefined);
}
